import { useState } from 'react'
import { Link, useLocation, useMatch, useResolvedPath } from 'react-router-dom'
import { ChevronRight, Star } from 'lucide-react'
import { useModules } from '../../contexts/ModuleContext'
import { useAuth } from '../../contexts/AuthContext'
import { groupKeyForPathname, resolveGroupTarget } from './navGroups'

// localStorage helpers для pinned/recent/collapsed
export function lsGet(key, fallback) {
    try { const v = localStorage.getItem(key); return v !== null ? JSON.parse(v) : fallback } catch { return fallback }
}
export function lsSet(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)) } catch {}
}

export const MAX_RECENT = 5
export const LS_PINNED = 'onboardix_nav_pinned'   // string[]
export const LS_RECENT = 'onboardix_nav_recent'   // string[]
// Ключ намеренно сменён на v2 после перестройки меню (2026-07-28).
// Люди сворачивали разделы, пока пунктов было больше сотни, и это
// состояние пережило редизайн: разделы на 43 пункта открывались
// свёрнутыми, и новой структуры просто не было видно. Смена ключа
// один раз возвращает всем развёрнутый вид; дальше состояние снова
// запоминается как обычно.
const LS_COLLAPSED = 'onboardix_nav_collapsed_v2'

export function NavItem({ item, onNavigate, isPinned, onTogglePin, isGroupActive = false }) {
    const resolvedPath = useResolvedPath(item.to || '')
    const routeMatch = useMatch({ path: resolvedPath.pathname, end: item.end || resolvedPath.pathname === '/' })
    const isActive = item.activeOverride ?? Boolean(routeMatch || isGroupActive)
    // subLabel-сепараторы рендерятся как под-заголовок секции
    if (item.subLabel) {
        return <div className="sidebar__sub-label">{item.subLabel}</div>
    }
    return (
        <div className="nav-item-wrapper">
            <Link
                to={item.to}
                onClick={onNavigate}
                className={`nav-link ${isActive ? 'active' : ''}`}
                aria-current={isActive ? 'page' : undefined}
            >
                <item.icon size={18} />
                <span>{item.label}</span>
                {item.badge && (
                    <span className="nav-badge" style={item.badgeStyle || undefined}>
                        {item.badge}
                    </span>
                )}
            </Link>
            {onTogglePin && (
                <button
                    type="button"
                    className={`nav-pin-btn ${isPinned ? 'nav-pin-btn--active' : ''}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePin(item.to) }}
                    title={isPinned ? 'Открепить' : 'Закрепить'}
                    aria-label={`${isPinned ? 'Открепить' : 'Закрепить'}: ${item.label}`}
                    aria-pressed={Boolean(isPinned)}
                >
                    <Star size={11} />
                </button>
            )}
        </div>
    )
}

export function NavSection({ title, items, onNavigate, defaultCollapsed = false, pinnedRoutes, onTogglePin, effectiveUser }) {
    const { isModuleVisible } = useModules()
    const { user } = useAuth()
    const location = useLocation()
    const storageKey = `${LS_COLLAPSED}__${title}`

    const [collapsed, setCollapsed] = useState(() => lsGet(storageKey, defaultCollapsed))

    const activeGroup = groupKeyForPathname(location.pathname)
    const isVisible = (key) => isModuleVisible(key, effectiveUser || user)

    // Три вида записей:
    //   subLabel-сепаратор — всегда включаем (у него нет `to`);
    //   groupKey — пункт-группа, разворачивается в ссылку на первую ДОСТУПНУЮ
    //              вкладку; если недоступна ни одна — пункт не рисуется;
    //   обычный пункт — фильтруем по видимости его модуля.
    const visibleItems = items.map(item => {
        if (item.subLabel) return item
        if (item.groupKey) {
            const resolved = resolveGroupTarget(item.groupKey, isVisible)
            return resolved ? { ...resolved, isGroup: true } : null
        }
        return isVisible(item.moduleKey || item.to || 'dashboard') ? item : null
    }).filter(Boolean)
    // Секция не нужна если нет ни одного настоящего пункта
    const hasRealItems = visibleItems.some(i => !i.subLabel)
    if (!hasRealItems) return null

    const toggle = () => {
        const next = !collapsed
        setCollapsed(next)
        lsSet(storageKey, next)
    }

    return (
        <>
            <div className="sidebar__divider" />
            <button type="button" className="sidebar__section sidebar__section--toggle" onClick={toggle} aria-expanded={!collapsed} title={collapsed ? 'Развернуть' : 'Свернуть'}>
                <span className="sidebar__section-title">{title}</span>
                <ChevronRight
                    size={12}
                    className="sidebar__section-chevron"
                    style={{ transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)', transition: 'transform 0.18s' }}
                />
            </button>
            {!collapsed && visibleItems.map((item, idx) => (
                item.subLabel
                    ? <NavItem key={`sub-${idx}`} item={item} onNavigate={onNavigate} />
                    : <NavItem
                        key={item.groupKey || item.to}
                        item={item}
                        onNavigate={onNavigate}
                        isPinned={pinnedRoutes?.has(item.to)}
                        onTogglePin={onTogglePin}
                        isGroupActive={Boolean(item.isGroup && activeGroup === item.groupKey)}
                    />
            ))}
        </>
    )
}

// Заголовок-переключатель — общий для «Закреплённого» и «Недавних».
function CollapsibleHeader({ title, collapsed, onToggle }) {
    return (
        <>
            <div className="sidebar__divider" />
            <button
                type="button"
                className="sidebar__section sidebar__section--toggle"
                onClick={onToggle}
                aria-expanded={!collapsed}
                title={collapsed ? 'Развернуть' : 'Свернуть'}
            >
                <span className="sidebar__section-title">{title}</span>
                <ChevronRight
                    size={12}
                    className="sidebar__section-chevron"
                    style={{ transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)', transition: 'transform 0.18s' }}
                />
            </button>
        </>
    )
}

// Закреплённые разделы (сверху). Сворачиваются: у активного пользователя
// их набирается с десяток, и вся структура меню уезжает под них.
export function PinnedSection({ pinnedRoutes, allItems, onNavigate, onTogglePin }) {
    const storageKey = `${LS_COLLAPSED}__Закреплённое`
    const [collapsed, setCollapsed] = useState(() => lsGet(storageKey, false))
    if (!pinnedRoutes || pinnedRoutes.size === 0) return null
    const items = [...pinnedRoutes]
        .map(to => allItems.find(i => i.to === to))
        .filter(Boolean)
    if (items.length === 0) return null
    const toggle = () => { const next = !collapsed; setCollapsed(next); lsSet(storageKey, next) }
    return (
        <>
            <CollapsibleHeader title={`Закреплённые · ${items.length}`} collapsed={collapsed} onToggle={toggle} />
            {!collapsed && items.map(item => (
                <NavItem
                    key={item.to}
                    item={item}
                    onNavigate={onNavigate}
                    isPinned
                    onTogglePin={onTogglePin}
                />
            ))}
        </>
    )
}

// Recent shortcuts are bounded so the catalog remains easy to reach.
export function RecentSection({ recentRoutes, allItems, onNavigate }) {
    const storageKey = `${LS_COLLAPSED}__Недавние`
    const [collapsed, setCollapsed] = useState(() => lsGet(storageKey, false))
    const [showAll, setShowAll] = useState(false)
    if (!recentRoutes || recentRoutes.length === 0) return null
    const items = recentRoutes
        .map(to => allItems.find(i => i.to === to))
        .filter(Boolean)
    if (items.length === 0) return null
    const toggle = () => { const next = !collapsed; setCollapsed(next); lsSet(storageKey, next) }
    return (
        <>
            <CollapsibleHeader title="Недавние" collapsed={collapsed} onToggle={toggle} />
            {!collapsed && (showAll ? items : items.slice(0, 3)).map(item => (
                <NavItem key={item.to} item={item} onNavigate={onNavigate} />
            ))}
            {!collapsed && items.length > 3 && <button type="button" className="workspace-navigation__recent-more" aria-expanded={showAll}
                onClick={() => setShowAll(value => !value)}>{showAll ? 'Показать меньше' : 'Показать ещё'}</button>}
        </>
    )
}
